/**
 * useJobTemplates Hook
 *
 * Manages job templates for quick job creation:
 * - Save jobs as reusable templates
 * - Clone jobs from templates
 * - CRUD operations for templates
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  Timestamp,
  increment,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import type { JobTemplate } from '@/types/contractor';

interface UseJobTemplatesReturn {
  templates: JobTemplate[];
  isLoading: boolean;
  error: string | null;

  // CRUD operations
  createTemplate: (template: Omit<JobTemplate, 'id' | 'usageCount' | 'createdAt' | 'updatedAt' | 'contractorId' | 'createdBy'>) => Promise<string>;
  updateTemplate: (id: string, updates: Partial<JobTemplate>) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;
  incrementUsage: (id: string) => Promise<void>;

  // Helpers
  saveJobAsTemplate: (job: JobToTemplate, templateName: string, description?: string) => Promise<string>;
}

// Minimal job fields needed to create a template
interface JobToTemplate {
  permitType?: string | null;
  trade?: string;
  jobScope?: string | null;
  title?: string;
  description?: string;
  estimatedValue?: number;
  isOwnerBuilder?: boolean;
}

export function useJobTemplates(): UseJobTemplatesReturn {
  const { user, profile } = useAuth();
  const [templates, setTemplates] = useState<JobTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get contractor ID
  const contractorId = profile?.contractorId || user?.uid;

  // Subscribe to templates collection
  useEffect(() => {
    if (!contractorId) {
      setTemplates([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const templatesRef = collection(db, `contractors/${contractorId}/job_templates`);
    const q = query(templatesRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const templatesData: JobTemplate[] = [];
        snapshot.forEach((doc) => {
          templatesData.push({
            id: doc.id,
            ...doc.data(),
          } as JobTemplate);
        });
        setTemplates(templatesData);
        setIsLoading(false);
      },
      (err) => {
        console.error('Error fetching templates:', err);
        setError('Failed to load templates');
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [contractorId]);

  // Create a new template
  const createTemplate = useCallback(async (
    template: Omit<JobTemplate, 'id' | 'usageCount' | 'createdAt' | 'updatedAt' | 'contractorId' | 'createdBy'>
  ): Promise<string> => {
    if (!contractorId || !user) {
      throw new Error('Not authenticated');
    }

    const templatesRef = collection(db, `contractors/${contractorId}/job_templates`);

    const docRef = await addDoc(templatesRef, {
      ...template,
      contractorId,
      createdBy: user.uid,
      usageCount: 0,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    return docRef.id;
  }, [contractorId, user]);

  // Update an existing template
  const updateTemplate = useCallback(async (
    id: string,
    updates: Partial<JobTemplate>
  ): Promise<void> => {
    if (!contractorId) {
      throw new Error('Not authenticated');
    }

    const templateRef = doc(db, `contractors/${contractorId}/job_templates`, id);

    await updateDoc(templateRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  }, [contractorId]);

  // Delete a template
  const deleteTemplate = useCallback(async (id: string): Promise<void> => {
    if (!contractorId) {
      throw new Error('Not authenticated');
    }

    const templateRef = doc(db, `contractors/${contractorId}/job_templates`, id);
    await deleteDoc(templateRef);
  }, [contractorId]);

  // Increment usage count when template is used
  const incrementUsage = useCallback(async (id: string): Promise<void> => {
    if (!contractorId) {
      throw new Error('Not authenticated');
    }

    const templateRef = doc(db, `contractors/${contractorId}/job_templates`, id);

    await updateDoc(templateRef, {
      usageCount: increment(1),
      lastUsedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  }, [contractorId]);

  // Helper to save a job as a template
  const saveJobAsTemplate = useCallback(async (
    job: JobToTemplate,
    templateName: string,
    description?: string
  ): Promise<string> => {
    // Extract trade info from permitType or trade field
    const tradeCode = job.permitType || job.trade || 'general';
    const tradeName = getTradeDisplayName(tradeCode);

    const template: Omit<JobTemplate, 'id' | 'usageCount' | 'createdAt' | 'updatedAt' | 'contractorId' | 'createdBy'> = {
      name: templateName,
      description: description || job.description || undefined,
      permitType: job.permitType || 'general',
      tradeCode,
      tradeName,
      jobScope: job.jobScope || undefined,
      defaultPropertyType: undefined, // Could be enhanced to detect from job
      defaultCategory: undefined,
      estimatedValueRange: job.estimatedValue ? {
        min: Math.floor(job.estimatedValue * 0.8),
        max: Math.ceil(job.estimatedValue * 1.2),
      } : undefined,
    };

    return createTemplate(template);
  }, [createTemplate]);

  return {
    templates,
    isLoading,
    error,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    incrementUsage,
    saveJobAsTemplate,
  };
}

// Helper to get display name for trade codes
function getTradeDisplayName(tradeCode: string): string {
  const tradeNames: Record<string, string> = {
    electrical: 'Electrical',
    plumbing: 'Plumbing',
    mechanical: 'Mechanical/HVAC',
    building: 'Building',
    roofing: 'Roofing',
    pool: 'Pool/Spa',
    gas: 'Gas',
    solar: 'Solar',
    general: 'General',
  };

  return tradeNames[tradeCode.toLowerCase()] || tradeCode;
}
